/**
 * Warp Grep Harness Primitives
 *
 * Building blocks for custom agent harnesses.
 * Use these when you want to control the agent loop yourself.
 *
 * @example
 * ```typescript
 * import {
 *   parseFinishFiles,
 *   parseReadLines,
 *   extractPathFromCommand,
 *   formatToolResult,
 *   formatTurnMessage,
 *   formatListDirectoryTree,
 *   calculateContextBudget,
 *   buildInitialState,
 *   toolGrep,
 *   toolRead,
 *   toolListDirectory,
 *   toolGlob,
 *   resolveFinishFiles,
 *   AGENT_CONFIG,
 *   LocalRipgrepProvider,
 *   type ToolCall,
 *   type WarpGrepProvider,
 * } from '@morphllm/morphsdk/tools/warp-grep/harness';
 *
 * const provider = new LocalRipgrepProvider(repoRoot);
 * const messages = [
 *   { role: 'user', content: await buildInitialState(repoRoot, searchTerm, provider) },
 * ];
 *
 * // With OpenAI native tool calling, tool_calls come structured from the API response.
 * // Use parseFinishFiles, parseReadLines, extractPathFromCommand to parse string args.
 * ```
 */

import type { ToolCall, ToolCallRef, ToolName, FinishFileSpec, ChatMessage } from './agent/types.js';
import type { WarpGrepProvider, GrepResult, ReadResult, GlobResult, ListDirectoryEntry } from './providers/types.js';

// ════════════════════════════════════════════════════════════════════════════
// PARSING HELPERS
// ════════════════════════════════════════════════════════════════════════════

export { parseFinishFiles, parseReadLines, extractPathFromCommand } from './agent/parser.js';

// ════════════════════════════════════════════════════════════════════════════
// FORMATTING
// ════════════════════════════════════════════════════════════════════════════

export { formatAgentToolOutput as formatToolResult } from './agent/formatter.js';

// ════════════════════════════════════════════════════════════════════════════
// HELPERS - same functions runner.ts uses
// ════════════════════════════════════════════════════════════════════════════

export {
  formatTurnMessage,
  calculateContextBudget,
  buildInitialState,
  formatListDirectoryTree,
} from './agent/helpers.js';

// ════════════════════════════════════════════════════════════════════════════
// TOOL EXECUTORS
// ════════════════════════════════════════════════════════════════════════════

export { toolGrep } from './agent/tools/grep.js';
export { toolRead } from './agent/tools/read.js';
export { toolListDirectory } from './agent/tools/list_directory.js';
export { toolGlob } from './agent/tools/glob.js';
export { readFinishFiles, normalizeFinishFiles } from './agent/tools/finish.js';

// ════════════════════════════════════════════════════════════════════════════
// FINISH RESOLUTION (convenience wrapper)
// ════════════════════════════════════════════════════════════════════════════

import { readFinishFiles } from './agent/tools/finish.js';

export type ResolvedFile = { path: string; content: string };

export async function resolveFinishFiles(
  provider: WarpGrepProvider,
  files: FinishFileSpec[]
): Promise<ResolvedFile[]> {
  return readFinishFiles('', files, async (p, s?, e?) => {
    const r = await provider.read({ path: p, start: s, end: e });
    return r.lines.map((l) => {
      const idx = l.indexOf('|');
      return idx >= 0 ? l.slice(idx + 1) : l;
    });
  });
}

// ════════════════════════════════════════════════════════════════════════════
// CONFIG + CONSTANTS
// ════════════════════════════════════════════════════════════════════════════

export { AGENT_CONFIG, DEFAULT_EXCLUDES } from './agent/config.js';

// ════════════════════════════════════════════════════════════════════════════
// PROVIDER + TYPES
// ════════════════════════════════════════════════════════════════════════════

export { LocalRipgrepProvider } from './providers/local.js';

export type {
  ToolCall,
  ToolCallRef,
  ToolName,
  FinishFileSpec,
  ChatMessage,
  WarpGrepProvider,
  GrepResult,
  ReadResult,
  GlobResult,
  ListDirectoryEntry,
};
